// WordPress REST data layer for the headless front end.
// At build time (SSG / loaders run in Node) requests go to the absolute WP
// origin; in the browser they go same-origin (dev proxy / production same host).

const WP_ORIGIN = (import.meta.env.VITE_WP_ORIGIN as string) || 'https://mitohacker.com'
const BASE = import.meta.env.SSR ? `${WP_ORIGIN}/wp-json` : '/wp-json'

export type FaqItem = { q: string; a: string }
export type Reference = { text: string; url?: string }
export type ArticleExtra = {
  quick_answer?: string
  takeaways?: string[]
  faq?: FaqItem[]
  references?: Reference[]
  poll_subjects?: string[]
}

export type PollTally = { index: number; subject: string; up: number; down: number }
export type LeaderboardEntry = {
  compound: string
  slug: string
  /** post_id + subjectIndex identify the ONE canonical tally for this compound.
   *  A vote cast from anywhere (leaderboard, /supplements, a comparison article)
   *  must use these so it lands on the same meta keys as the compound's pillar poll. */
  post_id: number
  subject_index: number
  category: string
  up: number
  down: number
  total: number
}
/** References a key in src/lib/reviewers.ts. Absent/null means not yet
 *  medically reviewed — the front end must never show a reviewer badge in
 *  that case. */
export type ReviewedBy = { slug: string; date: string }

export type Article = {
  id: number
  slug: string
  title: string
  date: string
  modified: string
  contentHtml: string
  excerpt: string
  readingTime: number
  author: string
  category: string
  featuredImage?: string
  featuredImageAlt?: string
  extra: ArticleExtra
  polls: PollTally[]
  tags: string[]
  reviewedBy: ReviewedBy | null
}

export type ArticleCard = {
  slug: string
  title: string
  excerpt: string
  date: string
  modified: string
  readingTime: number
  category: string
  featuredImage?: string
  featuredImageAlt?: string
  totalVotes: number
  tags: string[]
  reviewedBy: ReviewedBy | null
}

/* eslint-disable @typescript-eslint/no-explicit-any */

// Decode HTML entities so WordPress-texturized text (titles, excerpts) renders as
// real characters instead of leaking literal "&#8217;" into React text nodes.
const NAMED_ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', hellip: '…',
  ldquo: '“', rdquo: '”', lsquo: '‘', rsquo: '’',
  ndash: '–', mdash: '—',
}
function decodeEntities(input: string): string {
  return (input || '').replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (m, body) => {
    if (body[0] === '#') {
      const code = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10)
      return Number.isFinite(code) ? String.fromCodePoint(code) : m
    }
    return NAMED_ENTITIES[body] ?? m
  })
}

/** Truncates to ~155 chars at a word boundary for SEO meta descriptions —
 *  Google's practical display limit is ~155-160 chars; a raw slice cuts
 *  mid-word and mid-sentence in search results. */
export function truncateMeta(text: string, max = 155): string {
  if (text.length <= max) return text
  const cut = text.slice(0, max)
  const lastSpace = cut.lastIndexOf(' ')
  return (lastSpace > 40 ? cut.slice(0, lastSpace) : cut).trim() + '…'
}

function stripTags(html: string): string {
  return decodeEntities((html || '').replace(/<[^>]+>/g, '')).replace(/\s+/g, ' ').trim()
}

function parseExtra(raw?: string): ArticleExtra {
  if (!raw) return {}
  try {
    return JSON.parse(raw) as ArticleExtra
  } catch {
    return {}
  }
}

function toPolls(p: any): PollTally[] {
  return Array.isArray(p.mh_votes) ? p.mh_votes : []
}

function toReviewedBy(p: any): ReviewedBy | null {
  const slug = p.meta?.mh_reviewer_slug
  if (!slug) return null
  return { slug, date: p.meta?.mh_review_date || '' }
}

/** _embedded['wp:term'] is [categories, tags]; tags may be absent on older payloads. */
function toTags(p: any): string[] {
  const groups = p._embedded?.['wp:term']
  if (!Array.isArray(groups)) return []
  const tagGroup = groups[1]
  return Array.isArray(tagGroup) ? tagGroup.map((t: any) => String(t?.name || '')).filter(Boolean) : []
}

function toArticle(p: any): Article {
  const embedded = p._embedded || {}
  const author = embedded.author?.[0]?.name || 'MitoHacker'
  const featuredMedia = embedded['wp:featuredmedia']?.[0]
  const term = embedded['wp:term']?.[0]?.[0]?.name
  const title = stripTags(p.title?.rendered ?? '')
  return {
    id: p.id,
    slug: p.slug,
    title,
    date: p.date,
    modified: p.modified,
    contentHtml: p.content?.rendered ?? '',
    excerpt: stripTags(p.excerpt?.rendered ?? ''),
    readingTime: p.mh_reading_time ?? 6,
    author,
    category: term || 'Guide',
    featuredImage: featuredMedia?.source_url,
    featuredImageAlt: featuredMedia?.alt_text || title,
    extra: parseExtra(p.meta?.mh_article_json),
    polls: toPolls(p),
    tags: toTags(p),
    reviewedBy: toReviewedBy(p),
  }
}

function toCard(p: any): ArticleCard {
  const embedded = p._embedded || {}
  const featuredMedia = embedded['wp:featuredmedia']?.[0]
  const term = embedded['wp:term']?.[0]?.[0]?.name
  const polls = toPolls(p)
  const title = stripTags(p.title?.rendered ?? '')
  return {
    slug: p.slug,
    title,
    excerpt: stripTags(p.excerpt?.rendered ?? ''),
    date: p.date,
    modified: p.modified,
    readingTime: p.mh_reading_time ?? 6,
    category: term || 'Guide',
    featuredImage: featuredMedia?.source_url,
    featuredImageAlt: featuredMedia?.alt_text || title,
    totalVotes: polls.reduce((sum, v) => sum + v.up + v.down, 0),
    tags: toTags(p),
    reviewedBy: toReviewedBy(p),
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

/**
 * Caps concurrent in-flight requests to the WP origin during SSR/build. The
 * WP origin runs on a small box behind PHP-FPM with pm.max_children=12 — a
 * full build's ~30 loaders firing at once (vite-react-ssg gives no control
 * over their scheduling) saturates that pool and requests start queuing
 * behind PHP-FPM itself, past our fetch timeout, with no ability to retry
 * out of it since the whole batch is congested together. Queuing client-side
 * here instead keeps us comfortably under the pool ceiling.
 */
// Lowered from 6 to 3 on 2026-08-19. The droplet is a 2 GB box running WordPress,
// MySQL and nginx together; at 6 the SSG build saturated it and unrelated article
// loaders timed out, prerendering real articles as "Article not found" shells.
// verify-build.mjs caught it, but a gentler build is the actual fix.
const WP_CONCURRENCY = 3
let active = 0
const queue: (() => void)[] = []
async function withConcurrencyLimit<T>(fn: () => Promise<T>): Promise<T> {
  if (active >= WP_CONCURRENCY) {
    await new Promise<void>((resolve) => queue.push(resolve))
  }
  active++
  try {
    return await fn()
  } finally {
    active--
    queue.shift()?.()
  }
}

/**
 * Retries transient failures (timeouts, 5xx, connection resets) up to 3 total
 * attempts with a short backoff before giving up. Paired with the concurrency
 * gate above: retries alone couldn't help when the entire batch was
 * congested together, since a retry just rejoined the same saturated queue.
 */
async function wpFetch(path: string, attempt = 1): Promise<any> {
  try {
    return await withConcurrencyLimit(async () => {
      const res = await fetch(`${BASE}${path}`, { signal: AbortSignal.timeout(20000) })
      if (!res.ok) throw new Error(`WP ${res.status} for ${path}`)
      return await res.json()
    })
  } catch (err) {
    if (attempt < 4) {
      await sleep(800 * attempt)
      return wpFetch(path, attempt + 1)
    }
    throw err
  }
}

export type Announcement = { enabled: boolean; mode?: string; text: string; link: string; accent?: string }

/** The announcement bar payload (baked at build, refreshed on the client). */
export async function getAnnouncement(): Promise<Announcement | null> {
  try {
    const a = await wpFetch('/mitohacker/v1/announcement')
    return a && a.enabled && a.text ? a : null
  } catch {
    return null
  }
}

/**
 * A fetch EXCEPTION (timeout, 5xx, network error — after wpFetch's own retries
 * are exhausted) is fundamentally different from a genuine "this doesn't
 * exist" result, but a bare `catch { return null }` used to conflate the two:
 * a transient hiccup during the static build silently became a page that
 * rendered as "not found" and shipped with a 200 status. During SSR/build
 * (import.meta.env.SSR), rethrow so the build fails loudly instead — that
 * forces a rebuild rather than deploying broken content. In the browser
 * (client-side navigation), degrade gracefully instead, since crashing a real
 * visitor's session over a network blip would be worse than a transient
 * "not found" they can retry.
 */
function handleFetchFailure(err: unknown): never | undefined {
  if (import.meta.env.SSR) throw err
  return undefined
}

/** All published post slugs (for SSG getStaticPaths). */
export async function getAllSlugs(): Promise<string[]> {
  try {
    const posts = await wpFetch('/wp/v2/posts?per_page=100&_fields=slug')
    return posts.map((p: any) => p.slug).filter((s: string) => s && s !== 'hello-world')
  } catch (err) {
    handleFetchFailure(err)
    return []
  }
}

/** A single article + a few related cards, ranked for topical relevance
 *  (same category first, then most recent) rather than just recency — a
 *  reader on a CoQ10 article should see other CoQ10/supplement pieces
 *  before an unrelated peptide article that merely happens to be newer. */
export async function getArticle(slug: string): Promise<{ article: Article; related: ArticleCard[] } | null> {
  try {
    const posts = await wpFetch(`/wp/v2/posts?slug=${encodeURIComponent(slug)}&_embed`)
    if (!posts.length) return null // genuine not-found: a valid response with no matching post
    const article = toArticle(posts[0])
    const pool = (await getAllArticles()).filter((a) => a.slug !== article.slug)
    const related = pool
      .sort((a, b) => {
        const aMatch = a.category === article.category ? 1 : 0
        const bMatch = b.category === article.category ? 1 : 0
        if (aMatch !== bMatch) return bMatch - aMatch
        return new Date(b.date).getTime() - new Date(a.date).getTime()
      })
      .slice(0, 3)
    return { article, related }
  } catch (err) {
    handleFetchFailure(err)
    return null
  }
}

/** All article cards for the /articles hub. */
export async function getAllArticles(): Promise<ArticleCard[]> {
  try {
    const posts = await wpFetch('/wp/v2/posts?per_page=100&_embed&_fields=slug,title,excerpt,date,modified,mh_reading_time,mh_votes,meta,_links,_embedded')
    return posts.filter((p: any) => p.slug !== 'hello-world').map(toCard)
  } catch (err) {
    handleFetchFailure(err)
    return []
  }
}

/** Articles a given medical reviewer has signed off on, newest review first. */
export async function getArticlesByReviewer(reviewerSlug: string): Promise<ArticleCard[]> {
  const all = await getAllArticles()
  return all
    .filter((a) => a.reviewedBy?.slug === reviewerSlug)
    .sort((a, b) => (b.reviewedBy?.date || '').localeCompare(a.reviewedBy?.date || ''))
}

/** Top 10 leaderboard entries for the homepage (build-time bake; client refreshes separately). */
// Memoized: the SSG build calls this from every article loader plus /supplements
// and /leaderboard. Without the cache that is 45+ identical requests fired at
// WordPress inside one build, which overloaded it and made unrelated article
// loaders time out and prerender as "not found".
let leaderboardCache: Promise<LeaderboardEntry[]> | null = null

export async function getLeaderboard(): Promise<LeaderboardEntry[]> {
  if (!leaderboardCache) {
    leaderboardCache = wpFetch('/mitohacker/v1/leaderboard').catch(() => [] as LeaderboardEntry[])
  }
  return leaderboardCache
}

/**
 * Compounds from the leaderboard that this article substantively covers, minus its
 * own poll subjects (those already render the full VoteWidget). Lets a comparison
 * article such as "PQQ vs CoQ10" show a votable row for BOTH compounds, each wired
 * to that compound's canonical tally rather than a new one.
 *
 * Self-evolving by design: it matches against whatever compounds exist on the
 * leaderboard, so a new compound pillar is picked up everywhere it is discussed
 * without editing any article.
 */
export function findReferencedCompounds(
  article: Pick<Article, 'title' | 'contentHtml' | 'polls'>,
  entries: LeaderboardEntry[],
  minMentions = 3,
): string[] {
  const own = new Set(article.polls.map((p) => p.subject.toLowerCase()))
  const text = `${article.title} ${stripTags(article.contentHtml)}`.toLowerCase()
  const seen = new Set<string>()
  const hits: { name: string; n: number }[] = []
  for (const e of entries) {
    const key = e.compound.toLowerCase()
    if (own.has(key) || seen.has(key)) continue
    const needle = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const n = (text.match(new RegExp(`(^|[^a-z0-9])${needle}([^a-z0-9]|$)`, 'g')) || []).length
    if (n >= minMentions) {
      seen.add(key)
      hits.push({ name: e.compound, n })
    }
  }
  return hits.sort((a, b) => b.n - a.n).map((h) => h.name)
}

/** Split article HTML into heading-based sections and build a table of contents.
 *  Adds stable ids to h2/h3 for anchor links. */
export function processContent(html: string): { html: string; toc: { id: string; text: string; level: number }[] } {
  const toc: { id: string; text: string; level: number }[] = []
  const used = new Set<string>()
  const slugify = (t: string) => {
    // A CSS identifier cannot start with a digit, so `#600-mg` throws
    // "The string did not match the expected pattern" in WebKit the moment
    // anything passes it to querySelector. Prefix numeric-leading ids.
    let base = t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'section'
    if (/^[0-9]/.test(base)) base = `s-${base}`
    let s = base
    let i = 2
    while (used.has(s)) s = `${base}-${i++}`
    used.add(s)
    return s
  }
  const out = html.replace(/<(h2|h3)([^>]*)>([\s\S]*?)<\/\1>/gi, (_m, tag: string, attrs: string, inner: string) => {
    const text = stripTags(inner)
    const id = slugify(text)
    toc.push({ id, text, level: tag === 'h2' ? 2 : 3 })
    const cleaned = attrs.replace(/\sid="[^"]*"/i, '')
    return `<${tag}${cleaned} id="${id}">${inner}</${tag}>`
  })
  return { html: out, toc }
}
